import type { CSSProperties, ReactNode } from 'react';
import type { SDUIComponent, BindingContext } from '@fias/sdui-types';
import { evaluateExpression } from '@fias/expression-eval';
import { useArcheAction } from '../hooks/useArcheAction';
import { useArcheContext } from '../hooks/useArcheContext';

// Import all component implementations
import {
  Container,
  Card,
  Grid,
  Flex,
  FormGroup,
  Spacer,
  Divider,
  Collapsible,
  Accordion,
  Panel,
  SplitPane,
} from '../components/layout';
import { Heading, Text, Label, Badge } from '../components/typography';
import { Markdown } from '../components/typography/Markdown';
import {
  TextInput,
  TextArea,
  NumberInput,
  Select,
  Checkbox,
  RadioGroup,
  FileInput,
  FilePicker,
} from '../components/inputs';
import { BulletListInput } from '../components/inputs/BulletListInput';
import { ReviewList } from '../components/inputs/ReviewList';
import { Icon, Image, Table, Loading, Skeleton, EmptyState } from '../components/display';
import { Button, ButtonGroup, Link, Stepper, ProgressBar, Tabs } from '../components/navigation';
import { Alert } from '../components/feedback';
import {
  AIOutput,
  CostPerformanceSlider,
  EntityPicker,
  CategoryPicker,
  ChatInterface,
} from '../components/fias';

// Event handler types
type ActionReferenceEvent = { actionId: string; payload?: Record<string, unknown> };
type ActionSequenceEvent = { actions: string[] };
type EventHandlerDef = ActionReferenceEvent | ActionSequenceEvent;

// Type for component props with common SDUI structure
export interface SDUIComponentProps {
  id: string;
  props: Record<string, unknown>;
  style?: Record<string, unknown>;
  children?: ReactNode;
  events?: Record<string, EventHandlerDef>;
}

/**
 * Component types that render as plain <div>s (or other block elements).
 * These don't need the flex-sizing wrapper that replaced/form elements require,
 * because a plain div already honors flex-basis: 0 correctly.
 */
const DIV_BASED_COMPONENTS = new Set([
  'Container',
  'Card',
  'Grid',
  'Flex',
  'FormGroup',
  'Spacer',
  'Divider',
  'Collapsible',
  'Accordion',
  'Panel',
  'SplitPane',
  'ButtonGroup',
  'Tabs',
  'Stepper',
  'ProgressBar',
  'Heading',
  'Text',
  'Label',
  'Badge',
  'Alert',
  'Loading',
  'Skeleton',
  'EmptyState',
  'CostPerformanceSlider',
  'EntityPicker',
  'CategoryPicker',
  'Markdown',
  'BulletListInput',
  'ReviewList',
  'AIOutput',
  'ChatInterface',
]);

// Map of component type to React component
const componentMap: Record<string, React.ComponentType<SDUIComponentProps>> = {
  // Layout
  Container,
  Card,
  Grid,
  Flex,
  FormGroup,
  Spacer,
  Divider,
  Collapsible,
  Accordion,
  Panel,
  SplitPane,
  // Typography
  Heading,
  Text,
  Label,
  Badge,
  Markdown,
  // Inputs
  TextInput,
  TextArea,
  NumberInput,
  Select,
  Checkbox,
  RadioGroup,
  FileInput,
  FilePicker,
  BulletListInput,
  ReviewList,
  // Display
  Icon,
  Image,
  Table,
  Loading,
  Skeleton,
  EmptyState,
  // Navigation
  Button,
  ButtonGroup,
  Link,
  Stepper,
  ProgressBar,
  Tabs,
  // Feedback
  Alert,
  // FIAS-specific
  AIOutput,
  CostPerformanceSlider,
  EntityPicker,
  CategoryPicker,
  ChatInterface,
};

// Build a case-insensitive lookup map (lowercase -> component)
// This allows both "Button" and "button" to work
const componentMapLowercase: Record<string, React.ComponentType<SDUIComponentProps>> = {};
for (const [key, value] of Object.entries(componentMap)) {
  componentMapLowercase[key.toLowerCase()] = value;
}
// Add common aliases
componentMapLowercase['input'] = TextInput;
componentMapLowercase['form'] = Container;

/**
 * Look up a component by type, case-insensitively
 */
function getComponent(type: string): React.ComponentType<SDUIComponentProps> | undefined {
  // Try exact match first (for performance), then lowercase
  return componentMap[type] || componentMapLowercase[type.toLowerCase()];
}

// Unknown component fallback
function UnknownComponent({ type, id }: { type: string; id: string }) {
  return (
    <div className="p-4 border-2 border-dashed border-red-300 bg-red-50 rounded text-red-600">
      <p className="font-mono text-sm">Unknown component: {type}</p>
      <p className="text-xs text-red-400">ID: {id}</p>
    </div>
  );
}

export interface ComponentRendererProps {
  component: SDUIComponent;
}

// Type guard to check if component has children
function hasChildren(
  component: SDUIComponent,
): component is SDUIComponent & { children?: SDUIComponent[] } {
  return 'children' in component;
}

// Type guard to check if component has events
function hasEvents(component: SDUIComponent): component is SDUIComponent & {
  events?: Record<string, EventHandlerDef>;
} {
  return 'events' in component;
}

/**
 * Resolve a binding expression like {{input.currentStep === 0}}
 * Delegates to @fias/expression-eval for full operator support
 * (comparisons, arithmetic, ternaries, string methods, etc.)
 */
function resolveBinding(value: string, context: BindingContext): unknown {
  const expr = value.slice(2, -2).trim();

  const result = evaluateExpression(expr, {
    input: context.input ?? {},
    output: context.output ?? {},
    payload: {},
  });

  return result.success ? result.value : undefined;
}

/**
 * Resolve a single value that may contain a binding expression.
 * Handles strings, nested objects, and arrays recursively.
 */
function resolveValue(value: unknown, context: BindingContext): unknown {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('{{') && trimmed.endsWith('}}')) {
      return resolveBinding(trimmed, context);
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((element) => resolveValue(element, context));
  }

  if (value && typeof value === 'object') {
    return resolveBindingsInObject(value as Record<string, unknown>, context);
  }

  return value;
}

/**
 * Recursively resolve bindings in an object
 */
function resolveBindingsInObject(
  obj: Record<string, unknown>,
  context: BindingContext,
): Record<string, unknown> {
  const resolved: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    resolved[key] = resolveValue(value, context);
  }
  return resolved;
}

export function ComponentRenderer({ component }: ComponentRendererProps) {
  const { triggerAction, executeActionSequence } = useArcheAction();
  const { context } = useArcheContext();

  // Check visibility — resolve binding expression if needed, hide when falsy
  const visibleRaw = (component as unknown as Record<string, unknown>).visible;
  if (visibleRaw !== undefined) {
    let isVisible: unknown = visibleRaw;
    if (typeof isVisible === 'string' && isVisible.startsWith('{{') && isVisible.endsWith('}}')) {
      isVisible = resolveBinding(isVisible, context);
    }
    if (!isVisible) {
      return null;
    }
  }

  const Component = getComponent(component.type);

  if (!Component) {
    return <UnknownComponent type={component.type} id={component.id} />;
  }

  // Build event handlers for the component
  const eventHandlers: Record<string, () => void> = {};
  if (hasEvents(component) && component.events) {
    console.log('[ComponentRenderer] Component has events:', component.id, component.events);
    for (const [eventName, eventDef] of Object.entries(component.events)) {
      // Check for action reference first (existing behavior) for backward compatibility
      if (eventDef && 'actionId' in eventDef) {
        // Handle single action reference
        const def = eventDef as { actionId: string; payload?: Record<string, unknown> };
        eventHandlers[eventName] = () => {
          console.log('[ComponentRenderer] Triggering action:', def.actionId);
          triggerAction(def.actionId, def.payload || {});
        };
      } else if (
        eventDef &&
        'actions' in eventDef &&
        Array.isArray((eventDef as { actions: string[] }).actions)
      ) {
        // Handle action sequence
        const def = eventDef as { actions: string[] };
        eventHandlers[eventName] = () => {
          console.log('[ComponentRenderer] Executing action sequence:', def.actions);
          executeActionSequence(def.actions);
        };
      }
    }
  }

  // Get children if they exist
  const children =
    hasChildren(component) && component.children
      ? component.children.map((child: SDUIComponent) => (
          <ComponentRenderer key={child.id} component={child} />
        ))
      : undefined;

  // Resolve bindings in props
  // Also include 'content' from component level if present (some generators put it there instead of in props)
  const componentWithContent = component as SDUIComponent & { content?: string };
  const baseProps = {
    ...component.props,
    ...(componentWithContent.content !== undefined
      ? { content: componentWithContent.content }
      : {}),
    ...eventHandlers,
  };
  const resolvedProps = resolveBindingsInObject(baseProps as Record<string, unknown>, context);

  // Resolve bindings in style if present
  const resolvedStyle = component.style
    ? resolveBindingsInObject(component.style as Record<string, unknown>, context)
    : undefined;

  // When a component has a numeric `flex` style (e.g. "2"), wrap it in a
  // <div> that handles the flex sizing. This is necessary because <button>
  // and other replaced/form elements don't reliably honor flex-basis: 0,
  // causing proportional sizing to break. The wrapper div handles flex
  // distribution correctly, and the inner component fills it.
  if (resolvedStyle && 'flex' in resolvedStyle && !DIV_BASED_COMPONENTS.has(component.type)) {
    const { flex, flexGrow, flexShrink, flexBasis, minWidth, ...innerStyle } = resolvedStyle;
    const wrapperStyle: Record<string, unknown> = {
      flex,
      minWidth: minWidth ?? 0,
    };
    // Only pass through explicit overrides if present
    if (flexGrow !== undefined) wrapperStyle.flexGrow = flexGrow;
    if (flexShrink !== undefined) wrapperStyle.flexShrink = flexShrink;
    if (flexBasis !== undefined) wrapperStyle.flexBasis = flexBasis;

    const fillStyle = { ...innerStyle, width: '100%', height: '100%' };
    return (
      <div style={wrapperStyle as CSSProperties}>
        <Component id={component.id} props={resolvedProps} style={fillStyle}>
          {children}
        </Component>
      </div>
    );
  }

  return (
    <Component id={component.id} props={resolvedProps} style={resolvedStyle}>
      {children}
    </Component>
  );
}

export default ComponentRenderer;
